import React, { useState, useEffect } from "react";
import io from "socket.io-client";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";
import SecureLS from "secure-ls";
import Swal from "sweetalert2";
import GenericViewHero from "./GenericViewHero";
import { validateOTP } from "../../component/OTPValidator";
import { getAuthCredentials } from "../../utils/auth";
import TitleSetter from "../../component/TitleSetter";

const socket = io(process.env.REACT_APP_BACKEND_URL, {
  transports: ["websocket", "polling"],
});

const frontDeskLS = new SecureLS();
const userLS = new SecureLS({ encodingType: "aes" });

const FrontOfDeskView = () => {
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [showClaimed, setShowClaimed] = useState(false);
  const [claimedSearch, setClaimedSearch] = useState("");
  const [viewModuleBills, setViewModuleBills] = useState([]);
  const [mode, setMode] = useState("gross");
  const [allOrders, setAllOrders] = useState(() => ({
    newOrders: [],
    processingOrders: [],
    completedOrders: [],
    claimedOrders: [],
    historicalOrders: [],
  }));
  const [stockAlerts, setStockAlerts] = useState({
    outOfStockItems: [],
    lowStockItems: [],
    lowBills: [],
  });
  // This state holds countdown timers for orders (if needed)
  const [orderTimers, setOrderTimers] = useState({});

  // -----------------------
  // TIMER: Update countdown every second for New & Processing orders
  // -----------------------
  useEffect(() => {
    const timerInterval = setInterval(() => {
      setOrderTimers((prev) => {
        const updated = {};
        const all = [
          ...(allOrders.newOrders ?? []),
          ...(allOrders.processingOrders ?? []),
        ];

        for (const order of all) {
          const orderTime = new Date(order.orderTime).getTime();
          const now = Date.now();
          const diff = Math.floor((now - orderTime) / 1000);

          updated[order.orderNumber] = diff < 60 ? 60 - diff : diff;
        }

        return updated;
      });
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [allOrders.newOrders, allOrders.processingOrders]);

  // -----------------------
  // FETCH & SOCKET: Fetch orders on mount and set up socket listeners
  // Also, refetch orders every 2 minutes based on selected date.
  // -----------------------
  useEffect(() => {
    fetchOrders();
    fetchStockAlerts();

    const savedOrders = frontDeskLS.get("frontDeskOrders") || {};
    if (savedOrders) {
      setAllOrders({
        newOrders: savedOrders.newOrders || [],
        processingOrders: savedOrders.processingOrders || [],
        completedOrders: savedOrders.completedOrders || [],
        claimedOrders: savedOrders.claimedOrders || [],
        historicalOrders: savedOrders.historicalOrders || [],
      });
    }

    socket.on("orderList", (orderList) => {
      setAllOrders((prev) => {
        const updated = {
          ...prev,
          newOrders: orderList.filter((order) => order.status === "Accepted"),
        };
        frontDeskLS.set("frontDeskOrders", updated);
        return updated;
      });
    });

    socket.on("newOrder", (newOrder) => {
      const { posId } = getAuthCredentials();

      if (newOrder.posId !== posId) {
        console.log("newOrder.posId: ", newOrder.posId);
        console.log("posId: ", posId);
        console.warn("New order ignored: different POS ID", newOrder);
        return;
      }
      const processingOrder = {
        ...newOrder,
        status: "Processing",
      };

      setAllOrders((prev) => {
        const updated = {
          ...prev,
          processingOrders: [...prev.processingOrders, processingOrder],
        };
        frontDeskLS.set("frontDeskOrders", updated);
        return updated;
      });

      socket.emit("updateOrderStatus", {
        status: "Processing",
        order: processingOrder,
      });
    });

    socket.on("updateOrderStatus", ({ status, order }) => {
      const { posId } = getAuthCredentials();
      if (order.posId !== posId) {
        console.warn("Update ignored: different POS ID", order);
        return;
      }

      setAllOrders((prev) => {
        const updatedState = { ...prev };

        // Remove the order from all arrays
        Object.keys(updatedState).forEach((key) => {
          updatedState[key] = updatedState[key].filter(
            (o) =>
              (o.orderNumber || o.orderId) !==
              (order.orderNumber || order.orderId)
          );
        });

        // Add to correct array
        if (status === "Accepted") {
          updatedState.newOrders.push(order);
        } else if (status === "Processing") {
          updatedState.processingOrders.push(order);
        } else if (status === "Completed") {
          updatedState.completedOrders.push(order);
        } else if (status === "Claimed") {
          updatedState.claimedOrders.push(order);
        } else if (
          ["Cancelled", "Refunded", "Returned", "Voided"].includes(status)
        ) {
          updatedState.historicalOrders.push(order);
        }

        frontDeskLS.set("frontDeskOrders", updatedState);
        return updatedState;
      });
    });

    socket.on("updateHistoricalOrders", ({ order, action }) => {
      setAllOrders((prev) => {
        const historicalOrderExists = prev.historicalOrders.some(
          (hist) => String(hist.orderNumber) === String(order.orderNumber)
        );
        const updatedHistorical = historicalOrderExists
          ? prev.historicalOrders
          : [...prev.historicalOrders, { ...order, action }];
        const updatedState = { ...prev, historicalOrders: updatedHistorical };
        frontDeskLS.set("frontDeskOrders", updatedState);
        return updatedState;
      });
    });

    socket.on(
      "itemStatusUpdated",
      ({ orderId, menuItemId, optionId, completed }) => {
        setAllOrders((prev) => {
          const updateArray = (ordersArray) =>
            ordersArray.map((o) =>
              o._id === orderId || o.orderId === orderId
                ? {
                    ...o,
                    items: o.items.map((item) => {
                      if (item.menuItemId === menuItemId) {
                        if (optionId) {
                          return {
                            ...item,
                            options: item.options.map((opt) =>
                              opt.optionId === optionId
                                ? {
                                    ...opt,
                                    completed: completed ?? opt.completed,
                                  }
                                : opt
                            ),
                          };
                        } else {
                          return {
                            ...item,
                            completed: completed ?? item.completed,
                          };
                        }
                      }
                      return item;
                    }),
                  }
                : o
            );
          return {
            newOrders: updateArray(prev.newOrders),
            processingOrders: updateArray(prev.processingOrders),
            completedOrders: updateArray(prev.completedOrders),
            claimedOrders: updateArray(prev.claimedOrders),
            historicalOrders: updateArray(prev.historicalOrders),
          };
        });
      }
    );

    socket.on("callForAssistance", (payload) => {
      console.log("✅ received callForAssistance:", payload);

      Swal.fire({
        icon: "info",
        title: "Assistance Needed",
        text: payload.message,
        confirmButtonText: "Acknowledge",
      });
    });

    // Refetch orders every 2 minutes based on the selected date
    const intervalId = setInterval(() => {
      fetchOrders(selectedDate);
    }, 2 * 60 * 1000);

    const stockInterval = setInterval(() => {
      fetchStockAlerts();
    }, 2 * 60 * 1000);

    // const stockInterval = setInterval(() => {
    //   fetchStockAlerts();
    // }, 5 * 1000);

    return () => {
      socket.off("orderList");
      socket.off("newOrder");
      socket.off("updateOrderStatus");
      socket.off("updateHistoricalOrders");
      socket.off("itemStatusUpdated");
      socket.off("callForAssistance");
      clearInterval(intervalId);
      clearInterval(stockInterval);
    };
  }, [selectedDate]);

  // Initial fetch on mount: set selected date to today and fetch orders.
  useEffect(() => {
    const today = new Date().toLocaleDateString("en-CA", {
      timeZone: "Asia/Manila",
    });
    setSelectedDate(today);
    fetchOrders(today);
  }, []);

  const fetchOrders = async (date) => {
    if (!date) return;
    try {
      const { token } = getAuthCredentials();
      if (!token) {
        console.error("No auth token found.");
        return;
      }
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/order/fetch-orders?viewType=frontdesk&date=${date}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      console.log("data:", data);
      if (response.ok) {
        const formattedOrders = data.map((order) => ({
          ...order,
          orderNumber: order.orderName,
          receiptId: order._id,
        }));
        setMode(data[0]?.mode || "gross");
        setAllOrders({
          newOrders: formattedOrders.filter((o) => o.status === "Accepted"),
          processingOrders: formattedOrders.filter(
            (o) => o.status === "Processing"
          ),
          completedOrders: formattedOrders.filter(
            (o) => o.status === "Completed"
          ),
          claimedOrders: formattedOrders.filter((o) => o.status === "Claimed"),
          historicalOrders: formattedOrders.filter(
            (o) =>
              !["Accepted", "Processing", "Completed", "Claimed"].includes(
                o.status
              )
          ),
        });

        setOrderTimers((prevTimers) => {
          if (Object.keys(prevTimers).length > 0) return prevTimers;
          const timers = {};
          formattedOrders
            .filter((o) => ["Accepted", "Processing"].includes(o.status))
            .forEach((o) => {
              const orderTime = new Date(o.orderTime).getTime();
              const now = Date.now();
              timers[o.orderNumber] = Math.floor((now - orderTime) / 1000);
            });
          return timers;
        });

        frontDeskLS.set("frontDeskOrders", formattedOrders);
      } else {
        console.error("Failed to fetch front desk orders:", data.message);
      }
    } catch (error) {
      console.error("Error fetching front desk orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStockAlerts = async () => {
    try {
      const { token } = getAuthCredentials();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/order/get-stock-alerts`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await response.json();
      if (response.ok) {
        const existing = frontDeskLS.get("stockAlerts") || {
          outOfStockMenus: [],
          lowStockMenus: [],
          lowStockBills: [],
        };

        const mergeWithTimestamps = (newItems, oldItems, keyFn) => {
          const oldMap = new Map(oldItems.map((item) => [keyFn(item), item]));
          return newItems.map((item) => {
            const key = keyFn(item);
            return oldMap.has(key)
              ? { ...item, time: oldMap.get(key).time }
              : item;
          });
        };

        const updatedAlerts = {
          outOfStockMenus: mergeWithTimestamps(
            data.outOfStockMenus,
            existing.outOfStockMenus,
            (i) => i.name
          ),
          lowStockMenus: mergeWithTimestamps(
            data.lowStockMenus,
            existing.lowStockMenus,
            (i) => i.name
          ),
          lowStockBills: mergeWithTimestamps(
            data.lowStockBills,
            existing.lowStockBills,
            (i) => `${i.denomination}-${i.boxType}-${i.billType}`
          ),
        };

        frontDeskLS.set("stockAlerts", updatedAlerts);
        setStockAlerts(updatedAlerts);
      } else {
        console.error("Failed to fetch stock alerts:", data.message);
      }
    } catch (error) {
      console.error("Error fetching stock alerts:", error);
    }
  };

  const handleDateChange = (e) => {
    const date = e.target.value;
    setSelectedDate(date);
    fetchOrders(date);
  };

  // Toggle item or option completion
  const toggleItemCompletion = async (
    orderId2,
    menuItemId,
    order,
    optionId = null
  ) => {
    try {
      const { token } = getAuthCredentials();
      if (!token) {
        console.error("No auth token found.");
        return;
      }
      const orderId = orderId2 || order._id || order.orderId;
      const requestBody = optionId
        ? { orderId, menuItemId, optionId }
        : { orderId, menuItemId };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/order/toggle-item-status`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        }
      );
      const data = await response.json();
      if (response.ok) {
        setAllOrders((prev) => {
          const updateArray = (ordersArray) =>
            ordersArray.map((o) => {
              if (o._id === orderId || o.orderId === orderId) {
                return {
                  ...o,
                  items: o.items.map((item) => {
                    if (item.menuItemId === menuItemId) {
                      if (optionId) {
                        return {
                          ...item,
                          options: item.options.map((opt) =>
                            opt.optionId === optionId
                              ? {
                                  ...opt,
                                  completed:
                                    data.order.items
                                      .find((i) => i.menuItemId === menuItemId)
                                      ?.options.find(
                                        (o) => o.optionId === optionId
                                      )?.completed || false,
                                }
                              : opt
                          ),
                        };
                      } else {
                        return {
                          ...item,
                          completed:
                            data.order.items.find(
                              (i) => i.menuItemId === menuItemId
                            )?.completed || false,
                        };
                      }
                    }
                    return item;
                  }),
                };
              }
              return o;
            });
          const updatedOrders = {
            newOrders: updateArray(prev.newOrders),
            processingOrders: updateArray(prev.processingOrders),
            completedOrders: updateArray(prev.completedOrders),
            claimedOrders: updateArray(prev.claimedOrders),
            historicalOrders: updateArray(prev.historicalOrders),
          };
          frontDeskLS.set("frontDeskOrders", updatedOrders);
          return updatedOrders;
        });

        socket.emit("itemStatusUpdated", {
          orderId,
          menuItemId,
          optionId,
          completed: optionId
            ? data.order.items
                .find((i) => i.menuItemId === menuItemId)
                ?.options.find((o) => o.optionId === optionId)?.completed ||
              false
            : data.order.items.find((i) => i.menuItemId === menuItemId)
                ?.completed || false,
        });
      } else {
        console.error("Failed to toggle item status:", data.message);
      }
    } catch (error) {
      console.error("Error toggling item status:", error);
    }
  };

  const updateOrderStatus = async (orderId, status, orderObj = null) => {
    try {
      const { token } = getAuthCredentials();
      if (!token) {
        console.error("No auth token found.");
        return;
      }
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/order/update-order-status`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ orderId, status }),
        }
      );
      const data = await response.json();
      if (response.ok) {
        const now = new Date().toISOString();

        const updatedOrder = {
          ...(orderObj || {}),
          orderId,
          status,
          ...(status === "Claimed" ? { FOHorderTime: now } : {}),
        };

        socket.emit("updateOrderStatus", { status, order: updatedOrder });
        setAllOrders((prev) => {
          const updatedState = { ...prev };
          Object.keys(updatedState).forEach((key) => {
            updatedState[key] = updatedState[key].filter(
              (o) =>
                (o.orderNumber || o.orderId) !==
                (updatedOrder.orderNumber || updatedOrder.orderId)
            );
          });
          if (status === "Processing") {
            updatedState.processingOrders.push(updatedOrder);
          } else if (status === "Completed") {
            updatedState.completedOrders.push(updatedOrder);
          } else if (status === "Accepted") {
            updatedState.newOrders.push(updatedOrder);
          } else if (status === "Claimed") {
            updatedState.claimedOrders.push(updatedOrder);
          } else if (
            ["Cancelled", "Refunded", "Returned", "Voided"].includes(status)
          ) {
            updatedState.historicalOrders.push(updatedOrder);
          }
          frontDeskLS.set("frontDeskOrders", updatedState);
          return updatedState;
        });
        socket.emit("updateOrderStatus", { status, order: updatedOrder });
        console.log(`✅ Order ${orderId} updated to ${status}`);
      } else {
        console.error("❌ Failed to update order status:", data.message);
      }
    } catch (error) {
      console.error("❌ Error updating order status:", error);
    }
  };

  const handleCancelOrder = (order) => {
    const receiptId = order.receipt || order.receiptId;
    validateOTP(`Cancel Order + ${receiptId}`)
      .then((isValid) => {
        if (isValid) {
          Swal.fire({
            title: "Confirm Cancellation",
            text: "Are you sure you want to cancel this order?",
            icon: "warning",
            input: "textarea",
            inputPlaceholder: "Enter cancellation remarks (optional)...",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, cancel it!",
          }).then((result) => {
            if (result.isConfirmed) {
              const remarks = result.value || "";
              const { token } = getAuthCredentials();
              fetch(
                `${process.env.REACT_APP_BACKEND_URL}/v1/api/order/cancel-order`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Accept: "application/pdf",
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({ receiptId, remarks }),
                }
              )
                .then(async (res) => {
                  if (!res.ok) {
                    const errorMsg = await res.text();
                    throw new Error(errorMsg || "Failed to cancel order.");
                  }
                  return res.blob();
                })
                .then((pdfBlob) => {
                  const pdfUrl = window.URL.createObjectURL(pdfBlob);
                  window.open(pdfUrl, "_blank");
                  setAllOrders((prev) => {
                    const removeMatch = (o) =>
                      [o._id, o.orderId, o.receipt].includes(receiptId);
                    const cancelledOrder = [
                      ...prev.newOrders,
                      ...prev.processingOrders,
                      ...prev.completedOrders,
                      ...prev.claimedOrders,
                    ].find((o) => removeMatch(o));
                    if (!cancelledOrder) {
                      console.log(
                        `Order with Receipt ID ${receiptId} not found.`
                      );
                      return prev;
                    }
                    const updatedState = {
                      ...prev,
                      newOrders: prev.newOrders.filter((o) => !removeMatch(o)),
                      processingOrders: prev.processingOrders.filter(
                        (o) => !removeMatch(o)
                      ),
                      completedOrders: prev.completedOrders.filter(
                        (o) => !removeMatch(o)
                      ),
                      claimedOrders: prev.claimedOrders.filter(
                        (o) => !removeMatch(o)
                      ),
                      historicalOrders: [
                        ...prev.historicalOrders,
                        { ...cancelledOrder, salesType: "Cancelled", remarks },
                      ],
                    };
                    frontDeskLS.set("frontDeskOrders", updatedState);
                    const actualOrderId =
                      cancelledOrder._id || cancelledOrder.orderId || receiptId;
                    updateOrderStatus(
                      actualOrderId,
                      "Cancelled",
                      cancelledOrder
                    );
                    return updatedState;
                  });
                })
                .catch((error) => {
                  console.error("Error calling cancel order endpoint:", error);
                });
            }
          });
        }
      })
      .catch((error) => {
        console.error("OTP validation cancelled or failed:", error);
      });
  };

  const handleClaimOrder = (orderId) => {
    setAllOrders((prev) => {
      const claimedOrder = prev.completedOrders.find((o) => o._id === orderId);
      if (!claimedOrder) return prev;

      const now = new Date().toISOString();
      const claimedOrderWithTime = {
        ...claimedOrder,
        FOHorderTime: now,
      };

      updateOrderStatus(orderId, "Claimed", claimedOrderWithTime);

      const updated = {
        ...prev,
        completedOrders: prev.completedOrders.filter((o) => o._id !== orderId),
        claimedOrders: [claimedOrderWithTime, ...prev.claimedOrders],
      };

      frontDeskLS.set("frontDeskOrders", updated);
      return updated;
    });
  };

  const handleReturnOrder = (order) => {
    const receiptId = order.receipt || order.receiptId;
    validateOTP(`Return Order + ${receiptId}`)
      .then((isValid) => {
        if (isValid) {
          Swal.fire({
            title: "Confirm Return",
            text: "Are you sure you want to return this order?",
            icon: "question",
            input: "textarea",
            inputPlaceholder: "Enter return remarks (optional)...",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, return it!",
          }).then((result) => {
            if (result.isConfirmed) {
              const remarks = result.value || "";
              const { token } = getAuthCredentials();
              fetch(
                `${process.env.REACT_APP_BACKEND_URL}/v1/api/order/return-order`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Accept: "application/pdf",
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({ receiptId, remarks }),
                }
              )
                .then(async (res) => {
                  if (!res.ok) {
                    const errorMsg = await res.text();
                    throw new Error(errorMsg || "Failed to return order.");
                  }
                  return res.blob();
                })
                .then((pdfBlob) => {
                  const pdfUrl = window.URL.createObjectURL(pdfBlob);
                  window.open(pdfUrl, "_blank");
                  setAllOrders((prev) => {
                    const removeMatch = (o) =>
                      [o._id, o.orderId, o.receipt].includes(receiptId);
                    const returnedOrder = prev.claimedOrders.find((o) =>
                      removeMatch(o)
                    );
                    if (!returnedOrder) {
                      console.log(
                        `Order with Receipt ID ${receiptId} not found.`
                      );
                      return prev;
                    }
                    const updatedHistorical = prev.historicalOrders.some((o) =>
                      removeMatch(o)
                    )
                      ? prev.historicalOrders.map((h) =>
                          removeMatch(h)
                            ? {
                                ...returnedOrder,
                                salesType: "Returned",
                                remarks,
                              }
                            : h
                        )
                      : [
                          ...prev.historicalOrders,
                          { ...returnedOrder, salesType: "Returned", remarks },
                        ];
                    const updatedState = {
                      ...prev,
                      claimedOrders: prev.claimedOrders.filter(
                        (o) => !removeMatch(o)
                      ),
                      historicalOrders: updatedHistorical,
                    };
                    frontDeskLS.set("frontDeskOrders", updatedState);
                    const actualOrderId =
                      returnedOrder._id || returnedOrder.orderId || receiptId;
                    updateOrderStatus(actualOrderId, "Returned", returnedOrder);
                    return updatedState;
                  });
                })
                .catch((error) => {
                  console.error("Error calling return order endpoint:", error);
                });
            }
          });
        }
      })
      .catch((error) => {
        console.error("OTP validation cancelled or failed:", error);
      });
  };

  const handleRefundOrder = (order) => {
    const receiptId = order.receipt || order.receiptId;
    validateOTP(`Refund Order + ${receiptId}`)
      .then((isValid) => {
        if (isValid) {
          Swal.fire({
            title: "Confirm Refund",
            text: "Are you sure you want to refund this order?",
            icon: "question",
            input: "textarea",
            inputPlaceholder: "Enter refund remarks (optional)...",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, refund it!",
          }).then((result) => {
            if (result.isConfirmed) {
              const remarks = result.value || "";
              const { token } = getAuthCredentials();
              fetch(
                `${process.env.REACT_APP_BACKEND_URL}/v1/api/order/refund-order`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Accept: "application/pdf",
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({ receiptId, remarks }),
                }
              )
                .then(async (res) => {
                  if (!res.ok) {
                    const errorMsg = await res.text();
                    throw new Error(errorMsg || "Failed to refund order.");
                  }
                  return res.blob();
                })
                .then((pdfBlob) => {
                  const pdfUrl = window.URL.createObjectURL(pdfBlob);
                  window.open(pdfUrl, "_blank");
                  setAllOrders((prev) => {
                    const removeMatch = (o) =>
                      [o._id, o.orderId, o.receipt].includes(receiptId);
                    const refundedOrder = prev.claimedOrders.find((o) =>
                      removeMatch(o)
                    );
                    if (!refundedOrder) {
                      console.log(
                        `Order with Receipt ID ${receiptId} not found.`
                      );
                      return prev;
                    }
                    const updatedHistorical = prev.historicalOrders.some((o) =>
                      removeMatch(o)
                    )
                      ? prev.historicalOrders.map((h) =>
                          removeMatch(h)
                            ? {
                                ...refundedOrder,
                                salesType: "Refunded",
                                remarks,
                              }
                            : h
                        )
                      : [
                          ...prev.historicalOrders,
                          { ...refundedOrder, salesType: "Refunded", remarks },
                        ];
                    const updatedState = {
                      ...prev,
                      claimedOrders: prev.claimedOrders.filter(
                        (o) => !removeMatch(o)
                      ),
                      historicalOrders: updatedHistorical,
                    };
                    frontDeskLS.set("frontDeskOrders", updatedState);
                    const actualOrderId =
                      refundedOrder._id || refundedOrder.orderId || receiptId;
                    updateOrderStatus(actualOrderId, "Refunded", refundedOrder);
                    return updatedState;
                  });
                })
                .catch((error) => {
                  console.error("Error calling refund order endpoint:", error);
                });
            }
          });
        }
      })
      .catch((error) => {
        console.error("OTP validation cancelled or failed:", error);
      });
  };

  const handleVoidOrder = (order, actionLabel) => {
    const receiptId = order.receipt || order.receiptId;
    validateOTP(`Void Order + ${receiptId}`)
      .then((result) => {
        if (result?.status !== "success") return;
        {
          Swal.fire({
            title: `Confirm ${actionLabel}`,
            text: `Are you sure you want to ${actionLabel.toLowerCase()} this order?`,
            icon: "warning",
            input: "textarea",
            inputPlaceholder: `Enter ${actionLabel.toLowerCase()} remarks (optional)...`,
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: `Yes, ${actionLabel.toLowerCase()} it!`,
          }).then((result) => {
            if (result.isConfirmed) {
              const remarks = result.value || "";
              const { token } = getAuthCredentials();
              fetch(
                `${process.env.REACT_APP_BACKEND_URL}/v1/api/order/void-order`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Accept: "application/pdf",
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({ receiptId, remarks }),
                }
              )
                .then(async (res) => {
                  if (!res.ok) {
                    const errorMsg = await res.text();
                    throw new Error(errorMsg || "Failed to void order.");
                  }
                  return res.blob();
                })
                .then((pdfBlob) => {
                  const pdfUrl = window.URL.createObjectURL(pdfBlob);
                  window.open(pdfUrl, "_blank");
                  setAllOrders((prev) => {
                    const removeMatch = (o) =>
                      [o._id, o.orderId, o.receipt, o.receiptId].includes(
                        receiptId
                      );
                    const affectedOrder = [
                      ...prev.newOrders,
                      ...prev.processingOrders,
                      ...prev.completedOrders,
                      ...prev.claimedOrders,
                    ].find((o) => removeMatch(o));
                    if (!affectedOrder) {
                      console.log(
                        `Order with Receipt ID ${receiptId} not found.`
                      );
                      return prev;
                    }
                    const updatedState = {
                      ...prev,
                      newOrders: prev.newOrders.filter((o) => !removeMatch(o)),
                      processingOrders: prev.processingOrders.filter(
                        (o) => !removeMatch(o)
                      ),
                      completedOrders: prev.completedOrders.filter(
                        (o) => !removeMatch(o)
                      ),
                      claimedOrders: prev.claimedOrders.filter(
                        (o) => !removeMatch(o)
                      ),
                      historicalOrders: [
                        ...prev.historicalOrders,
                        {
                          ...affectedOrder,
                          salesType: actionLabel,
                          remarks,
                          status: "Voided",
                        },
                      ],
                    };
                    frontDeskLS.set("frontDeskOrders", updatedState);
                    const actualOrderId =
                      affectedOrder._id || affectedOrder.orderId || receiptId;
                    updateOrderStatus(actualOrderId, "Voided", {
                      ...affectedOrder,
                      salesType: actionLabel,
                      remarks,
                      status: "Voided",
                    });
                    return updatedState;
                  });
                })
                .catch((error) => {
                  console.error("Error calling void order endpoint:", error);
                });
            }
          });
        }
      })
      .catch((error) => {
        console.error("OTP validation cancelled or failed:", error);
      });
  };

  const {
    newOrders,
    processingOrders,
    completedOrders,
    claimedOrders,
    historicalOrders,
  } = allOrders;

  const validClaimedOrders = (claimedOrders ?? []).filter(
    (order) => (order?.items ?? []).length > 0
  );

  const filteredClaimedOrders = validClaimedOrders.filter((order) => {
    const search = claimedSearch.toLowerCase();
    return (
      String(order.orderNumber).toLowerCase().includes(search) ||
      String(order.tentNumber).toLowerCase().includes(search)
    );
  });

  const claimedOrdersWithTime = [...claimedOrders].filter(
    (order) => order.FOHorderTime && (order.BOHorderTime || order.createdAt)
  );

  const averageOrderTimeSeconds = claimedOrdersWithTime.length
    ? Math.round(
        claimedOrdersWithTime.reduce((sum, order) => {
          const start = new Date(
            order.BOHorderTime || order.createdAt
          ).getTime();
          const end = new Date(order.FOHorderTime || order.updatedAt).getTime();
          const diffSeconds = Math.max((end - start) / 1000, 0);
          return sum + diffSeconds;
        }, 0) / claimedOrdersWithTime.length
      )
    : 0;

  const averageOrderTime = averageOrderTimeSeconds;

  const allNonVoidedOrders = [
    ...newOrders,
    ...processingOrders,
    ...completedOrders,
    ...claimedOrders,
    ...historicalOrders,
  ].filter((order) => order.status !== "Voided");

  const firstShiftOrder = allNonVoidedOrders.find(order => order.shift);
  const grossSales = firstShiftOrder
    ? (mode === "net" ? firstShiftOrder.currentNetAmt : firstShiftOrder.currentAmt)
    : 0;

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TitleSetter title="Front Of House" />
      <Box mb={10} mt={1}>
        <GenericViewHero
          title="Front Of House"
          ongoingCount={processingOrders?.length || 0}
          stockAlerts={stockAlerts}
          date={selectedDate}
          averageOrderTime={averageOrderTime}
          onDateChange={(date) => {
            setSelectedDate(date);
            fetchOrders(date);
          }}
          grossSales={grossSales}
          mode={mode}
        />
      </Box>
      {/* <Box sx={{ marginBottom: 3, display: "flex", justifyContent: "center" }}>
        <TextField
          label="Select Date"
          type="date"
          value={selectedDate}
          onChange={handleDateChange}
          InputLabelProps={{ shrink: true }}
          fullWidth
          sx={{ maxWidth: 400 }}
        />
      </Box> */}

      {/* Bills Section */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          {viewModuleBills.map((bill, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{ padding: 2, textAlign: "center" }}>
                <CardContent>
                  <Typography variant="h6">
                    {bill.denomination} - {bill.boxType}
                  </Typography>
                  <Typography variant="body2">
                    Quantity: {bill.quantity}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "50vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              px: 3,
              mt: -7,
            }}
          >
            {/* Top Row: Two Columns */}
            <Box
              sx={{
                height: "70vh",
                minHeight: "70vh",
                overflowY: "auto",
                mt: 1,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" align="center" sx={{ mt: 1 }}>
                    Ongoing FOH Orders
                  </Typography>
                  <Box sx={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <Grid
                      container
                      spacing={2}
                      justifyContent="center"
                      sx={{ px: 2 }}
                    >
                      {[...processingOrders, ...completedOrders]
                        .sort(
                          (a, b) =>
                            new Date(a.createdAt) - new Date(b.createdAt)
                        )
                        .map((order, index) => {
                          const isProcessing = order.status === "Processing";
                          const isCompleted = order.status === "Completed";
                          const elapsed = Math.floor(
                            (Date.now() - new Date(order.orderTime)) / 1000
                          );
                          const isElapsed = elapsed >= 60;
                          const displaySeconds = Math.abs(elapsed - 60);
                          const isSolelyBeverage = order.items.every((item) =>
                            item.menuTags?.includes("beverage")
                          );

                          const salesTypeBg = {
                            "Dine-In": "#e8f5e9",
                            Delivery: "#fff3e0",
                            "Take-Out": "#e1f5fe",
                          };

                          return (
                            <Grid
                              item
                              xs={12}
                              sm={6}
                              md={4}
                              key={index}
                              sx={{ display: "flex" }}
                            >
                              <Card
                                sx={{
                                  mb: 2,
                                  display: "flex",
                                  flexDirection: "column",
                                  border:
                                    isElapsed && isProcessing
                                      ? "2px solid red"
                                      : "1px solid gray",
                                  backgroundColor:
                                    isElapsed && isProcessing
                                      ? "#ffe6e6"
                                      : "white",
                                  height: "100%",
                                  width: "100%",
                                }}
                              >
                                <Box
                                  sx={{
                                    mb: 1,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    backgroundColor:
                                      salesTypeBg[order.salesType] || "#f5f5f5",
                                    px: 2,
                                    py: 1.5,
                                  }}
                                >
                                  <Box>
                                    <Typography variant="h6">
                                      Order #: {order.orderNumber}
                                    </Typography>
                                    {(() => {
                                      const d = new Date(order.createdAt);
                                      if (isNaN(d.getTime())) return null;
                                      return (
                                        <Typography
                                          variant="body2"
                                          sx={{ mt: -0.5 }}
                                        >
                                          <strong>Time:</strong>{" "}
                                          {d.toLocaleTimeString("en-PH", {
                                            hour12: true,
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            second: "2-digit",
                                          })}
                                        </Typography>
                                      );
                                    })()}
                                    <Typography variant="body1">
                                      Tent Number: {order.tentNumber}
                                    </Typography>
                                    {isProcessing && (
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontWeight: isElapsed
                                            ? "bold"
                                            : "normal",
                                          color: isElapsed ? "red" : "inherit",
                                        }}
                                      >
                                        {(() => {
                                          const minutes = Math.floor(
                                            displaySeconds / 60
                                          );
                                          const seconds = displaySeconds % 60;
                                          return `${minutes
                                            .toString()
                                            .padStart(2, "0")}:${seconds
                                            .toString()
                                            .padStart(2, "0")}`;
                                        })()}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Box sx={{ textAlign: "right" }}>
                                    <Typography
                                      sx={{
                                        fontSize: "3rem",
                                        fontWeight: "bold",
                                        color:
                                          order.salesType === "Dine-In"
                                            ? "#2e7d32"
                                            : order.salesType === "Delivery"
                                            ? "orange"
                                            : "#0277bd",
                                        lineHeight: 1,
                                      }}
                                    >
                                      {order.salesType === "Dine-In"
                                        ? "DI"
                                        : order.salesType === "Delivery"
                                        ? "DEL"
                                        : "TO"}
                                    </Typography>
                                  </Box>
                                </Box>
                                <CardContent
                                  sx={{
                                    flexGrow: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <Typography variant="body1">
                                    Items:
                                  </Typography>
                                  <Box sx={{ pl: 1 }}>
                                    {[...order.items]
                                      .sort((a, b) => {
                                        const aIsBeverage =
                                          a.menuTags?.includes("beverage");
                                        const bIsBeverage =
                                          b.menuTags?.includes("beverage");
                                        return bIsBeverage - aIsBeverage;
                                      })
                                      .map((item, i) => (
                                        <Box
                                          key={i}
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            mb: 0.5,
                                          }}
                                        >
                                          {item.menuTags?.includes(
                                            "beverage"
                                          ) ? (
                                            <>
                                              <Checkbox
                                                checked={
                                                  item.completed || false
                                                }
                                                onChange={() =>
                                                  toggleItemCompletion(
                                                    order._id,
                                                    item.menuItemId,
                                                    order
                                                  )
                                                }
                                                sx={{
                                                  transform: "scale(1.5)",
                                                  mr: 1,
                                                  ml: -2,
                                                }}
                                              />
                                              <Typography
                                                sx={{
                                                  fontSize: "0.925rem",
                                                  textDecoration: item.completed
                                                    ? "line-through"
                                                    : "none",
                                                  color: item.completed
                                                    ? "green"
                                                    : "inherit",
                                                  wordBreak: "break-word",
                                                }}
                                              >
                                                - {`${item.amount} pc/s`}
                                                {item.code
                                                  ? ` [${item.code.slice(
                                                      0,
                                                      6
                                                    )}]`
                                                  : ""}
                                                {item.uniqueName &&
                                                item.uniqueName !==
                                                  item.name ? (
                                                  <span
                                                    style={{ color: "red" }}
                                                  >
                                                    {" "}
                                                    {item.uniqueName}
                                                  </span>
                                                ) : null}{" "}
                                                - {item.name}
                                              </Typography>
                                            </>
                                          ) : (
                                            <Typography
                                              sx={{
                                                fontSize: "0.925rem",
                                                textDecoration: item.completed
                                                  ? "line-through"
                                                  : "none",
                                                color: item.completed
                                                  ? "green"
                                                  : "inherit",
                                                wordBreak: "break-word",
                                              }}
                                            >
                                              - {`${item.amount} pc/s`}
                                              {item.code ? ` [${item.code.slice(0, 6)}]` : ""}
                                              {item.uniqueName ? (
                                                <span style={{ color: "red" }}>
                                                  {" "}
                                                  {item.uniqueName}
                                                </span>
                                              ) : null}{" "}
                                              - {item.name}
                                            </Typography>
                                          )}
                                        </Box>
                                      ))}

                                    {(order.items ?? []).map((item) =>
                                      Array.isArray(item.options) &&
                                      item.options.length > 0 ? (
                                        <Box
                                          key={item.menuItemId}
                                          sx={{
                                            pl: item.menuTags?.includes(
                                              "beverage"
                                            )
                                              ? 6
                                              : 2,
                                          }}
                                        >
                                          {[...(item.options ?? [])]
                                            .sort((a, b) => {
                                              const aIsBeverage =
                                                a.menuTags?.includes(
                                                  "beverage"
                                                );
                                              const bIsBeverage =
                                                b.menuTags?.includes(
                                                  "beverage"
                                                );
                                              return bIsBeverage - aIsBeverage;
                                            })
                                            .map((option, j) => (
                                              <Box
                                                key={j}
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  mb: 0.5,
                                                }}
                                              >
                                                {option.menuTags?.includes(
                                                  "beverage"
                                                ) ? (
                                                  <>
                                                    <Checkbox
                                                      checked={
                                                        option.completed ||
                                                        false
                                                      }
                                                      onChange={() =>
                                                        toggleItemCompletion(
                                                          order._id,
                                                          item.menuItemId,
                                                          order,
                                                          option.optionId
                                                        )
                                                      }
                                                      disabled={isCompleted}
                                                      sx={{
                                                        transform: "scale(1.5)",
                                                        mr: 1,
                                                      }}
                                                    />
                                                    <Typography
                                                      sx={{
                                                        fontSize: "0.925rem",
                                                        textDecoration:
                                                          option.completed
                                                            ? "line-through"
                                                            : "none",
                                                        color: option.completed
                                                          ? "green"
                                                          : "inherit",
                                                        wordBreak: "break-word",
                                                      }}
                                                    >
                                                      {`${
                                                        option.name
                                                      }: ${option.choices.join(
                                                        ", "
                                                      )}`}
                                                    </Typography>
                                                  </>
                                                ) : (
                                                  <Typography
                                                    sx={{
                                                      fontSize: "0.925rem",
                                                      textDecoration:
                                                        option.completed
                                                          ? "line-through"
                                                          : "none",
                                                      color: option.completed
                                                        ? "green"
                                                        : "inherit",
                                                      wordBreak: "break-word",
                                                    }}
                                                  >
                                                    -{" "}
                                                    {`${
                                                      option.name
                                                    }: ${option.choices.join(
                                                      ", "
                                                    )}`}
                                                  </Typography>
                                                )}
                                              </Box>
                                            ))}
                                        </Box>
                                      ) : null
                                    )}
                                  </Box>
                                </CardContent>

                                <Box
                                  sx={{
                                    px: 2,
                                    pb: 2,
                                    mt: "auto",
                                    display: "flex",
                                    gap: 2,
                                  }}
                                >
                                  {isProcessing && isSolelyBeverage && (
                                    <Button
                                      fullWidth
                                      variant="contained"
                                      color="success"
                                      onClick={() =>
                                        updateOrderStatus(
                                          order.orderId || order.receiptId,
                                          "Completed",
                                          order
                                        )
                                      }
                                      sx={{ borderRadius: "12px" }}
                                    >
                                      Order Complete
                                    </Button>
                                  )}
                                  {isCompleted && (
                                    <Button
                                      fullWidth
                                      variant="contained"
                                      color="success"
                                      onClick={() =>
                                        handleClaimOrder(order._id)
                                      }
                                      disabled={order.items
                                        .filter((i) =>
                                          i.menuTags?.includes("beverage")
                                        )
                                        .some((i) => !i.completed)}
                                    >
                                      Claim Order
                                    </Button>
                                  )}
                                </Box>
                              </Card>
                            </Grid>
                          );
                        })}
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </Box>
            {/* Claimed Orders (full-width) */}
            {validClaimedOrders.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="h6" align="center" sx={{ mt: 3 }}>
                      Historical Orders
                    </Typography>
                    <IconButton
                      onClick={() => setShowClaimed((prev) => !prev)}
                      sx={{ mt: 2 }}
                    >
                      {showClaimed ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>

                  <Collapse in={showClaimed}>
                    <TextField
                      label="Search Order # or Tent #"
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={{ mb: 2 }}
                      value={claimedSearch}
                      onChange={(e) => setClaimedSearch(e.target.value)}
                    />
                    <TableContainer
                      component={Paper}
                      sx={{ maxHeight: "70vh", mt: 2 }}
                    >
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <strong>Date/Time</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Order #</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Tent #</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Sales Type</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Items</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Total</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Actions</strong>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredClaimedOrders
                            .sort(
                              (a, b) =>
                                new Date(b.FOHorderTime || b.createdAt) -
                                new Date(a.FOHorderTime || a.createdAt)
                            )
                            .map((order, index) => (
                              <TableRow
                                key={index}
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "rgba(0, 0, 0, 0.05)",
                                  },
                                }}
                              >
                                <TableCell>
                                  {new Date(
                                    order.updatedAt || order.createdAt
                                  ).toLocaleString()}
                                </TableCell>
                                <TableCell>{order.orderNumber}</TableCell>
                                <TableCell>
                                  {order.tentNumber || "N/A"}
                                </TableCell>
                                <TableCell>
                                  {order.salesType || "N/A"}
                                </TableCell>
                                <TableCell>
                                  <ul
                                    style={{ margin: 0, paddingLeft: "1rem" }}
                                  >
                                    {order.items.map((item, i) => (
                                      <li
                                        key={i}
                                        style={{
                                          color: item.completed
                                            ? "green"
                                            : "black",
                                        }}
                                      >
                                        <span
                                          style={{
                                            textDecoration: item.completed
                                              ? "line-through"
                                              : "none",
                                          }}
                                        >
                                          {`${item.amount}x ${item.name}`}
                                        </span>
                                        {Array.isArray(item.options) &&
                                          item.options.length > 0 && (
                                            <ul style={{ margin: 0 }}>
                                              {item.options.map((opt, j) => (
                                                <li
                                                  key={j}
                                                  style={{
                                                    color: opt.completed
                                                      ? "green"
                                                      : "inherit",
                                                  }}
                                                >
                                                  <span
                                                    style={{
                                                      textDecoration:
                                                        opt.completed
                                                          ? "line-through"
                                                          : "none",
                                                    }}
                                                  >
                                                    {`${
                                                      opt.name
                                                    }: ${opt.choices.join(
                                                      ", "
                                                    )}`}
                                                  </span>
                                                </li>
                                              ))}
                                            </ul>
                                          )}
                                      </li>
                                    ))}
                                  </ul>
                                </TableCell>
                                <TableCell>
                                  ₱{order.total?.toFixed(2)}
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    const orderDate = new Date(
                                      order.createdAt || order.orderTime
                                    );
                                    const now = new Date();
                                    const daysDiff = Math.floor(
                                      (now - orderDate) / (1000 * 60 * 60 * 24)
                                    );
                                    const isVoidDisabled = daysDiff >= 8;

                                    return (
                                      <Tooltip
                                        title={
                                          isVoidDisabled
                                            ? "7 days have already passed for Voiding this Order"
                                            : ""
                                        }
                                      >
                                        <span>
                                          <Button
                                            variant="contained"
                                            color="error"
                                            onClick={() =>
                                              handleVoidOrder(order, "Void")
                                            }
                                            disabled={isVoidDisabled}
                                          >
                                            VOID Order
                                          </Button>
                                        </span>
                                      </Tooltip>
                                    );
                                  })()}
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Collapse>
                </CardContent>
              </Card>
            )}
            {/* Historical Orders (table mode) */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="h6" align="center" sx={{ mt: 3 }}>
                    Voided Orders
                  </Typography>
                  <IconButton
                    onClick={() => setShowHistory((prev) => !prev)}
                    sx={{ mt: 2 }}
                  >
                    {showHistory ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                <Collapse in={showHistory}>
                  {historicalOrders.length > 0 ? (
                    <TableContainer
                      component={Paper}
                      sx={{ maxHeight: "70vh", mt: 2 }}
                    >
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <strong>Date/Time</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Order #</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Tent #</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Sales Type</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Items</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Total</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Status</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Remarks</strong>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {[...historicalOrders]
                            .sort(
                              (a, b) =>
                                new Date(b.updatedAt || b.createdAt) -
                                new Date(a.updatedAt || a.createdAt)
                            )
                            .map((order, index) => (
                              <TableRow
                                key={index}
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "rgba(0, 0, 0, 0.05)",
                                  },
                                }}
                              >
                                <TableCell>
                                  {new Date(
                                    order.updatedAt || order.createdAt
                                  ).toLocaleString()}
                                </TableCell>
                                <TableCell>{order.orderNumber}</TableCell>
                                <TableCell>
                                  {order.tentNumber || "N/A"}
                                </TableCell>
                                <TableCell>
                                  {order.salesType || "N/A"}
                                </TableCell>
                                <TableCell>
                                  <ul
                                    style={{ margin: 0, paddingLeft: "1rem" }}
                                  >
                                    {order.items.map((item, i) => (
                                      <li
                                        key={i}
                                        style={{
                                          color: item.completed
                                            ? "green"
                                            : "black",
                                        }}
                                      >
                                        <span
                                          style={{
                                            textDecoration: item.completed
                                              ? "line-through"
                                              : "none",
                                          }}
                                        >
                                          {`${item.amount}x ${item.name}`}
                                        </span>
                                        {Array.isArray(item.options) &&
                                          item.options.length > 0 && (
                                            <ul style={{ margin: 0 }}>
                                              {item.options.map((opt, j) => (
                                                <li
                                                  key={j}
                                                  style={{
                                                    color: opt.completed
                                                      ? "green"
                                                      : "inherit",
                                                  }}
                                                >
                                                  <span
                                                    style={{
                                                      textDecoration:
                                                        opt.completed
                                                          ? "line-through"
                                                          : "none",
                                                    }}
                                                  >
                                                    {`${
                                                      opt.name
                                                    }: ${opt.choices.join(
                                                      ", "
                                                    )}`}
                                                  </span>
                                                </li>
                                              ))}
                                            </ul>
                                          )}
                                      </li>
                                    ))}
                                  </ul>
                                </TableCell>
                                <TableCell>
                                  ₱{order.total?.toFixed(2)}
                                </TableCell>
                                <TableCell>{order.status}</TableCell>
                                <TableCell>{order.remarks || "—"}</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography sx={{ mt: 2 }}>
                      No historical orders available
                    </Typography>
                  )}
                </Collapse>
              </CardContent>
            </Card>
          </Box>
        </>
      )}
    </Box>
  );
};

export default FrontOfDeskView;